import { DirectApiError } from '@/lib/api/direct-client';

/** Backend error code for a shared-mailbox registration refused by the pre-persist probe. */
export const OUTLOOK_SHARED_MAILBOX_UNREACHABLE_CODE =
  'OUTLOOK_SHARED_MAILBOX_UNREACHABLE';

/** Why registration was refused — mirrors the backend's `OutlookSharedMailboxProbeFailure`. */
export type OutlookSharedMailboxProbeFailure =
  | 'mailbox_not_found'
  | 'principal_access_denied'
  | 'probe_failed';

export interface OutlookSharedMailboxUnreachable {
  reason: OutlookSharedMailboxProbeFailure | undefined;
  /**
   * The sentence to show. **Rendered as given** — the backend owns this copy, so
   * the card never switches on `reason` to build its own and drift from it, the
   * same split the blocked-send reasons use (VALE-348).
   */
  reasonMessage: string | undefined;
}

/**
 * Narrow a thrown registration error to "Vale couldn't reach that mailbox", or
 * `null` if it is anything else (a 403 for a non-admin, a validation 400, a
 * network failure — all of which keep their existing generic handling).
 *
 * Before VALE-382 registration never contacted Microsoft: the row was saved as
 * `connected` and the card then read "Vale is sending and receiving through …"
 * over a mailbox that answered nothing. The backend now probes it first and
 * refuses with a coded **409** carrying the reason, so the administrator is told
 * which of the two fixable things is actually wrong — a mistyped address, or an
 * authorising account without Full Access — instead of a generic "couldn't
 * register" that names no next step.
 */
export function getOutlookSharedMailboxUnreachable(
  error: unknown
): OutlookSharedMailboxUnreachable | null {
  if (!(error instanceof DirectApiError)) return null;
  if (error.response?.code !== OUTLOOK_SHARED_MAILBOX_UNREACHABLE_CODE) {
    return null;
  }

  return {
    reason: error.response.reason as
      | OutlookSharedMailboxProbeFailure
      | undefined,
    reasonMessage: error.response.reasonMessage
  };
}
